import { Controller, type Control } from "react-hook-form";
import { Input } from "@/components/Input";
import { Select } from "@/components/Select";
import { Textarea } from "@/components/Textarea";
import { Switch } from "@/components/Switch";
import { SearchableMultiSelect } from "@/components/SearchableMultiSelect";
import { useProviderContext } from "@/pages/Providers/useProviderApi";
import type {
    Catalogs,
    FieldDefinition,
    ProviderValues,
} from "@/pages/Providers/types";

interface ProviderFieldProps {
    definition: FieldDefinition;
    control: Control<ProviderValues>;
    catalogs: Catalogs;
    managed: boolean;
    locked: boolean;
    onIdentityBlur: () => void;
}

export const ProviderField = ({
    definition,
    control,
    catalogs,
    managed,
    locked,
    onIdentityBlur,
}: ProviderFieldProps) => {
    const { t } = useProviderContext();
    const { name, type, catalog, required } = definition;
    const options = catalog ? catalogs[catalog] : [];
    const identity = [
        "name",
        "tax_id",
        "email",
        "phone",
        "address",
        "address_extra",
        "postal_code",
        "city",
        "province",
        "country_id",
    ].includes(name);
    const label = `${t(`providers.fields.${name}`)}${managed ? ` · ${t("providers.ui.bc_short")}` : ""}`;
    const help = locked
        ? t("providers.ui.bc_locked")
        : managed
          ? t("providers.ui.bc_overwrite")
          : "";

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState }) => {
                const onBlur = (): void => {
                    field.onBlur();
                    if (identity) {
                        onIdentityBlur();
                    }
                };
                const error = fieldState.error?.message
                    ? t(fieldState.error.message)
                    : "";
                const value = Array.isArray(field.value)
                    ? ""
                    : (field.value ?? "");
                switch (type) {
                    case "boolean":
                        return (
                            <Switch
                                label={label}
                                checked={Boolean(field.value)}
                                onChange={field.onChange}
                                disabled={locked}
                                description={help}
                            />
                        );
                    case "multiple": {
                        const ids = Array.isArray(field.value)
                            ? field.value
                            : [];
                        const available = [
                            ...options,
                            ...ids
                                .filter(
                                    (id) =>
                                        !options.some(
                                            (option) => Number(option.id) === id
                                        )
                                )
                                .map((id) => ({
                                    id,
                                    name: t("providers.ui.unknown_catalog", {
                                        id,
                                    }),
                                })),
                        ];
                        return (
                            <SearchableMultiSelect
                                label={label}
                                options={available}
                                value={ids}
                                onChange={field.onChange}
                                disabled={locked}
                                helperText={error || help}
                            />
                        );
                    }
                    case "select": {
                        const available = options.map((option) => ({
                            value: Number(option.id),
                            label: option.name,
                        }));
                        if (
                            field.value != null &&
                            !available.some(
                                (option) => option.value === Number(field.value)
                            )
                        ) {
                            available.push({
                                value: Number(field.value),
                                label: t("providers.ui.unknown_catalog", {
                                    id: field.value,
                                }),
                            });
                        }
                        const select = (id: number): void => {
                            field.onChange(id ? Number(id) : null);
                            if (identity) {
                                onIdentityBlur();
                            }
                        };
                        return (
                            <div>
                                <Select<number>
                                    label={label}
                                    value={Number(field.value ?? 0)}
                                    options={available}
                                    onChange={select}
                                    searchable
                                    required={required}
                                    disabled={locked}
                                    clearable={!required}
                                    placeholder={t("providers.ui.select")}
                                    error={error}
                                />
                                {help && (
                                    <p className="mt-1 text-xs text-slate-500">
                                        {help}
                                    </p>
                                )}
                            </div>
                        );
                    }
                    case "textarea":
                        return (
                            <Textarea
                                name={name}
                                label={label}
                                aria-label={label}
                                value={String(value)}
                                onChange={field.onChange}
                                onBlur={onBlur}
                                disabled={locked}
                                error={error}
                                rows={4}
                            />
                        );
                    default: {
                        const changeValue = (text: string): void =>
                            field.onChange(
                                type === "number"
                                    ? text === ""
                                        ? null
                                        : Number(text)
                                    : text
                            );
                        return (
                            <Input
                                name={name}
                                label={label}
                                aria-label={label}
                                value={String(value)}
                                onChange={(event) =>
                                    changeValue(event.target.value)
                                }
                                onBlur={onBlur}
                                ref={field.ref}
                                required={required}
                                disabled={locked}
                                error={error}
                                helperText={help}
                                type={
                                    ["date", "number", "email", "tel"].includes(
                                        type ?? ""
                                    )
                                        ? type
                                        : "text"
                                }
                                min={type === "number" ? 0 : undefined}
                                step={
                                    type === "number"
                                        ? name === "retention"
                                            ? 1
                                            : 0.01
                                        : undefined
                                }
                            />
                        );
                    }
                }
            }}
        />
    );
};
